"use client";

import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Trash2, Mail, User, Clock, CheckCircle2, XCircle, RefreshCw, Inbox } from "lucide-react";
import { projectId, publicAnonKey } from "../../../../utils/supabase/info";

interface Message {
  id: string;
  name: string;
  email: string;
  message: string;
  date: string;
  isRead: boolean;
}

export function MessagesManager() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMessages = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-5f13ee94/messages`, {
        headers: { "Authorization": `Bearer ${publicAnonKey}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const deleteMessage = async (id: string) => {
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-5f13ee94/messages/${id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${publicAnonKey}` }
      });
      if (response.ok) {
        setMessages(messages.filter(m => m.id !== id));
      }
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  const toggleRead = async (id: string, currentRead: boolean) => {
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-5f13ee94/messages/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${publicAnonKey}`
        },
        body: JSON.stringify({ isRead: !currentRead })
      });
      if (response.ok) {
        const updatedMsg = await response.json();
        setMessages(messages.map(m => m.id === id ? updatedMsg : m));
      }
    } catch (error) {
      console.error("Error updating message:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="p-20 text-center">
        <RefreshCw size={40} className="mx-auto text-violet-500 animate-spin mb-4" />
        <p className="text-neutral-500 font-bold uppercase tracking-widest text-xs">Loading Messages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.length > 0 ? (
        messages.map((msg) => (
          <motion.div 
            layout
            key={msg.id}
            className={`p-6 bg-neutral-900/40 border border-neutral-800 rounded-3xl hover:bg-neutral-800/30 transition-colors flex flex-col md:flex-row gap-6 ${!msg.isRead ? 'border-l-4 border-violet-500' : ''}`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-neutral-950 rounded-lg text-neutral-400">
                  <User size={16} />
                </div>
                <span className="font-black uppercase tracking-tight text-white">{msg.name}</span>
                <span className="text-neutral-600 mx-2">•</span>
                <div className="flex items-center gap-1.5 text-neutral-500 text-sm">
                  <Mail size={14} /> {msg.email}
                </div>
              </div>
              <p className="text-neutral-400 leading-relaxed text-sm mb-4">{msg.message}</p>
              <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-neutral-600">
                <span className="flex items-center gap-1"><Clock size={12} /> {new Date(msg.date).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="flex md:flex-col justify-end gap-2">
              <button 
                onClick={() => toggleRead(msg.id, msg.isRead)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-tighter transition-all ${msg.isRead ? 'bg-neutral-800 text-neutral-400' : 'bg-violet-600/20 text-violet-400 hover:bg-violet-600/30'}`}
              >
                {msg.isRead ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                {msg.isRead ? 'Mark Unread' : 'Mark Read'}
              </button>
              <button 
                onClick={() => deleteMessage(msg.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-tighter bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-all"
              >
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </motion.div>
        ))
      ) : (
        <div className="p-20 text-center bg-neutral-900/20 rounded-3xl border border-dashed border-neutral-800">
          <Inbox size={48} className="mx-auto text-neutral-700 mb-4" />
          <p className="text-neutral-500 font-bold uppercase tracking-widest text-xs">No messages found</p>
        </div>
      )}
    </div>
  );
}